import type { UseLiveQueryStatus } from "@tanstack/react-db";

export type CollectionQueryStatus = "error" | "pending" | "success";

export interface CollectionErrorUtils {
  readonly lastError?: unknown;
  readonly isError?: boolean;
  readonly errorCount?: number;
  readonly clearError?: () => unknown;
}

export interface CollectionErrorSource {
  readonly utils?: unknown;
}

export interface CollectionErrorState {
  readonly clearError: (() => Promise<void>) | null;
  readonly errorCount: number;
  readonly isError: boolean;
  readonly lastError: unknown;
}

export interface CollectionQueryState {
  readonly collection?: CollectionErrorSource | null;
  readonly enabled: boolean;
  readonly error?: unknown;
  readonly isLoading: boolean;
  readonly status: UseLiveQueryStatus;
}

export interface CollectionQueryErrorState {
  readonly collection?: CollectionErrorSource | null;
  readonly enabled: boolean;
  readonly error?: unknown;
  readonly isLoading?: boolean;
  readonly status: UseLiveQueryStatus;
}

/**
 * Stable identity for the `clearError` wrapper we expose.
 *
 * `getCollectionErrorState` / `combineCollectionErrorState` run un-memoized on
 * every render across many hooks, and some hooks thread `clearError` through
 * their own `useMemo` deps. A fresh closure per call would force those memos —
 * and any downstream effect/callback — to recompute every render, which is the
 * exact class of accidental re-render we want to avoid on a high-frequency
 * state UI. We key the cache on the collection utils object plus the underlying
 * `rawClearError` reference so the wrapper is stable and still preserves
 * prototype-method receivers from `@tanstack/query-db-collection`.
 */
const clearErrorWrapperByReceiver = new WeakMap<
  object,
  WeakMap<() => unknown, () => Promise<void>>
>();

function readCollectionErrorUtils(
  collection: CollectionErrorSource | null | undefined,
): CollectionErrorUtils | null {
  const utils = collection?.utils;
  return utils !== null && typeof utils === "object"
    ? (utils as CollectionErrorUtils)
    : null;
}

function hasLastError(value: unknown): boolean {
  return value !== undefined && value !== null;
}

function wrapClearError(
  rawClearError: (() => unknown) | undefined,
  receiver: object | null,
): (() => Promise<void>) | null {
  if (typeof rawClearError !== "function") {
    return null;
  }

  const cacheOwner = receiver ?? rawClearError;
  let wrappersByRawFn = clearErrorWrapperByReceiver.get(cacheOwner);
  if (!wrappersByRawFn) {
    wrappersByRawFn = new WeakMap<() => unknown, () => Promise<void>>();
    clearErrorWrapperByReceiver.set(cacheOwner, wrappersByRawFn);
  }

  const existing = wrappersByRawFn.get(rawClearError);
  if (existing) {
    return existing;
  }

  const wrapper = async (): Promise<void> => {
    await rawClearError.call(receiver ?? undefined);
  };
  wrappersByRawFn.set(rawClearError, wrapper);
  return wrapper;
}

export function getCollectionErrorState(
  collection: CollectionErrorSource | null | undefined,
): CollectionErrorState {
  const utils = readCollectionErrorUtils(collection);
  const lastError = utils?.lastError;
  const errorCount =
    typeof utils?.errorCount === "number" && Number.isFinite(utils.errorCount)
      ? utils.errorCount
      : 0;
  const isError = utils?.isError === true || hasLastError(lastError);

  return {
    clearError: wrapClearError(utils?.clearError, utils),
    errorCount: isError ? Math.max(errorCount, 1) : 0,
    isError,
    lastError,
  };
}

export function getCollectionLastError(
  collection: CollectionErrorSource | null | undefined,
): unknown {
  return getCollectionErrorState(collection).lastError;
}

export function combineCollectionErrorState(
  collections: readonly (CollectionErrorSource | null | undefined)[],
): CollectionErrorState {
  const states = collections.map((collection) =>
    getCollectionErrorState(collection),
  );
  const clearFns = states.flatMap((state) =>
    state.clearError ? [state.clearError] : [],
  );

  return {
    clearError: combineClearErrorFns(clearFns),
    errorCount: states.reduce((sum, state) => sum + state.errorCount, 0),
    isError: states.some((state) => state.isError),
    lastError:
      states.find((state) => state.isError && hasLastError(state.lastError))
        ?.lastError ??
      states.find((state) => hasLastError(state.lastError))?.lastError,
  };
}

/**
 * Memoize the combined fan-out wrapper by the tuple of underlying (already
 * stable) clearError wrappers. Because each underlying wrapper is referentially
 * stable (keyed on its utils receiver plus raw fn via {@link wrapClearError}),
 * a key derived from those references is itself stable across renders as long
 * as the set of contributing collections doesn't change — so consumers threading the
 * combined clearError through useMemo/useEffect deps don't recompute every
 * render.
 */
const clearErrorIdRegistry = new WeakMap<() => Promise<void>, number>();
let nextClearErrorId = 0;
const combinedClearErrorByKey = new Map<string, () => Promise<void>>();

function combineClearErrorFns(
  fns: readonly (() => Promise<void>)[],
): (() => Promise<void>) | null {
  if (fns.length === 0) {
    return null;
  }

  let cacheKey = String(fns.length);
  for (const fn of fns) {
    let id = clearErrorIdRegistry.get(fn);
    if (id === undefined) {
      id = nextClearErrorId++;
      clearErrorIdRegistry.set(fn, id);
    }
    cacheKey += `:${id}`;
  }

  const existing = combinedClearErrorByKey.get(cacheKey);
  if (existing) {
    return existing;
  }

  const wrapper = async (): Promise<void> => {
    const results = await Promise.allSettled(
      fns.map((clearError) => clearError()),
    );
    const rejectedResult = results.find(
      (result): result is PromiseRejectedResult =>
        result.status === "rejected",
    );
    if (rejectedResult) {
      throw rejectedResult.reason;
    }
  };
  combinedClearErrorByKey.set(cacheKey, wrapper);
  return wrapper;
}

export function normalizeCollectionError(
  error: unknown,
  fallbackMessage: string,
): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return new Error(error);
  }

  return new Error(fallbackMessage);
}

/**
 * Pair `isError` and `error` so they never disagree.
 *
 * Simple hooks that fold collection-utility error state into a query's error
 * can end up with `isError: true` (e.g. the live-query result collection hit a
 * pipeline error) while `error` is `undefined` (because the source's
 * `utils.lastError` was unset). This normalizes the payload to a real `Error`
 * whenever `isError` is true, using `fallbackMessage` when no concrete error is
 * available.
 */
export function resolveCollectionErrorPayload(params: {
  error: unknown;
  fallbackMessage: string;
  isError: boolean;
}): Error | undefined {
  if (!params.isError) {
    return undefined;
  }

  return normalizeCollectionError(params.error, params.fallbackMessage);
}

/**
 * Combine live-query + collection-utility status into a single view status.
 *
 * Two kinds of "error" must be distinguished:
 *
 * - A **terminal query error** (`status === "error"`) is a definitive failure
 *   reported by TanStack Query on a source's own observer. It wins
 *   immediately, even if another source is still loading.
 * - A **sticky collection-utility error** (`utils.lastError`/`isError`) is set
 *   by `@tanstack/query-db-collection` when a `queryFn` rejects, but the
 *   collection lifecycle stays non-error (the handler calls `markReady()`), so
 *   the only in-flight signal is `isLoading`. While a retry/refetch is in
 *   flight we surface `pending` rather than pinning the view to the stale
 *   collection error; once nothing is loading, the sticky error wins.
 *
 * `utils.lastError` is also collection-wide, not per-subset: two hooks sharing
 * one collection instance (e.g. the dashboard + detail slices share the
 * portfolio `assetPrices`/`assets` instances) see the same `lastError`.
 * Per-subset error tracking would be the cleaner long-term fix but is not
 * exposed by the package today.
 */
export function combineCollectionQueryStatus(
  states: readonly CollectionQueryState[],
): CollectionQueryStatus {
  // Terminal query errors win immediately — do not mask a definitive failure
  // behind another source's loading state.
  if (
    states.some((state) => state.enabled && state.status === "error")
  ) {
    return "error";
  }

  // Loading (initial load or retry) wins over a sticky collection-utility
  // error so the view reports the in-flight retry rather than the stale error.
  if (
    states.some(
      (state) =>
        state.enabled &&
        (state.isLoading ||
          state.status === "idle" ||
          state.status === "loading"),
    )
  ) {
    return "pending";
  }

  // Nothing loading — a sticky collection-utility error now wins.
  if (
    states.some(
      (state) =>
        state.enabled &&
        getCollectionErrorState(state.collection).isError,
    )
  ) {
    return "error";
  }

  // A disabled/cleaned-up enabled source is still "pending" (no data yet).
  if (
    states.some(
      (state) =>
        state.enabled &&
        (state.status === "disabled" || state.status === "cleaned-up"),
    )
  ) {
    return "pending";
  }

  return "success";
}

export function combineCollectionQueryError(
  states: readonly CollectionQueryErrorState[],
  fallbackMessage: string,
): Error | null {
  const enabledStates = states.filter((state) => state.enabled);

  for (const state of enabledStates) {
    // Terminal query errors win immediately; they represent a concrete failed
    // observer rather than the package's sticky collection-wide lastError.
    if (state.status !== "error") {
      continue;
    }
    const collectionError = getCollectionErrorState(state.collection);
    return normalizeCollectionError(
      state.error ?? collectionError.lastError,
      fallbackMessage,
    );
  }

  const hasEnabledLoadingState = enabledStates.some(
    (state) =>
      state.isLoading === true ||
      state.status === "idle" ||
      state.status === "loading",
  );
  if (hasEnabledLoadingState) {
    return null;
  }

  for (const state of enabledStates) {
    const collectionError = getCollectionErrorState(state.collection);
    if (collectionError.isError) {
      return normalizeCollectionError(
        state.error ?? collectionError.lastError,
        fallbackMessage,
      );
    }
  }

  return null;
}
